import React, { useEffect, useState } from "react";
import { Badge, Button, Container, Form, Modal, Spinner } from "react-bootstrap";
import axios from "../utils/api";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";
const PAYMENT_OPTIONS = ["online", "check", "cash"];
const PAYMENT_BADGE = { online: "primary", check: "warning", cash: "success" };

const emptyVendor = { name: "", payment_method: "", payment_notes: "" };
const emptyContact = { name: "", role: "", phone: "", email: "" };

function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterVendor, setFilterVendor] = useState(""); // "" = all
  const [filterPayment, setFilterPayment] = useState(""); // "" = all
  const [expanded, setExpanded] = useState(() => new Set());

  /* vendor modal */
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendor);
  const [saving, setSaving] = useState(false);

  /* inline contact form */
  const [addingContactFor, setAddingContactFor] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContact);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}api/vendors`);
      if (res.data?.success) setVendors(res.data.vendors);
    } catch (err) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  /* ── Vendor CRUD ── */
  const openAddVendor = () => {
    setEditingVendor(null);
    setVendorForm(emptyVendor);
    setShowModal(true);
  };
  const openEditVendor = (v) => {
    setEditingVendor(v);
    setVendorForm({ name: v.name, payment_method: v.payment_method || "", payment_notes: v.payment_notes || "" });
    setShowModal(true);
  };
  const handleSaveVendor = async () => {
    if (!vendorForm.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editingVendor) {
        const res = await axios.put(`${API_URL}api/vendors/${editingVendor._id}`, vendorForm);
        if (res.data?.success) {
          setVendors((prev) => prev.map((v) => (v._id === editingVendor._id ? res.data.vendor : v)));
          toast.success("Vendor updated");
        }
      } else {
        const res = await axios.post(`${API_URL}api/vendors`, vendorForm);
        if (res.data?.success) {
          setVendors((prev) => [...prev, res.data.vendor].sort((a, b) => a.name.localeCompare(b.name)));
          toast.success("Vendor added");
        }
      }
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const handleDeleteVendor = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}api/vendors/${v._id}`);
      setVendors((prev) => prev.filter((x) => x._id !== v._id));
      toast.success("Vendor deleted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  /* ── Contact CRUD ── */
  const openAddContact = (vendorId) => {
    setAddingContactFor(vendorId);
    setEditingContact(null);
    setContactForm(emptyContact);
  };
  const openEditContact = (vendorId, c) => {
    setEditingContact({ vendorId, contactId: c._id });
    setAddingContactFor(null);
    setContactForm({ name: c.name || "", role: c.role || "", phone: c.phone || "", email: c.email || "" });
  };
  const cancelContact = () => {
    setAddingContactFor(null);
    setEditingContact(null);
    setContactForm(emptyContact);
  };
  const replaceVendor = (updated) => {
    setVendors((prev) => prev.map((v) => (v._id === updated._id ? updated : v)));
  };
  const handleSaveContact = async (vendorId) => {
    if (!contactForm.name.trim()) { toast.error("Contact name is required"); return; }
    try {
      if (editingContact) {
        const res = await axios.put(
          `${API_URL}api/vendors/${vendorId}/contact/${editingContact.contactId}`,
          contactForm
        );
        if (res.data?.success) { replaceVendor(res.data.vendor); toast.success("Contact updated"); }
      } else {
        const res = await axios.post(`${API_URL}api/vendors/${vendorId}/contact`, contactForm);
        if (res.data?.success) { replaceVendor(res.data.vendor); toast.success("Contact added"); }
      }
      cancelContact();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    }
  };
  const handleDeleteContact = async (vendorId, contactId) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      const res = await axios.delete(`${API_URL}api/vendors/${vendorId}/contact/${contactId}`);
      if (res.data?.success) { replaceVendor(res.data.vendor); toast.success("Contact removed"); }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  /* ── Contact inline form (rendered inline, not as a component) ── */
  const renderContactForm = (vendorId) => (
    <div className="d-flex flex-wrap gap-2 align-items-end mt-2 p-2 rounded" style={{ background: "#f0f4ff" }}>
      <Form.Group style={{ minWidth: 140, flex: 1 }}>
        <Form.Label className="mb-0 small">Name</Form.Label>
        <Form.Control size="sm" value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} />
      </Form.Group>
      <Form.Group style={{ minWidth: 120, flex: 1 }}>
        <Form.Label className="mb-0 small">Role</Form.Label>
        <Form.Control size="sm" value={contactForm.role} onChange={(e) => setContactForm((f) => ({ ...f, role: e.target.value }))} placeholder="Sales Rep, Delivery…" />
      </Form.Group>
      <Form.Group style={{ minWidth: 130, flex: 1 }}>
        <Form.Label className="mb-0 small">Phone</Form.Label>
        <Form.Control size="sm" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
      </Form.Group>
      <Form.Group style={{ minWidth: 160, flex: 1 }}>
        <Form.Label className="mb-0 small">Email</Form.Label>
        <Form.Control size="sm" type="email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
      </Form.Group>
      <div className="d-flex gap-1">
        <Button size="sm" variant="success" onClick={() => handleSaveContact(vendorId)}>Save</Button>
        <Button size="sm" variant="outline-secondary" onClick={cancelContact}>Cancel</Button>
      </div>
    </div>
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = vendors.filter((v) => {
    if (filterVendor && v._id !== filterVendor) return false;
    if (filterPayment && v.payment_method !== filterPayment) return false;
    return true;
  });

  if (loading) {
    return <Container className="mt-4 text-center py-5"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Vendors</h2>
        <Button variant="primary" size="sm" onClick={openAddVendor}>+ Add Vendor</Button>
      </div>

      {/* Filter bar */}
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-5">
            <Form.Label className="mb-1 small fw-semibold">Vendor</Form.Label>
            <Form.Select size="sm" value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}>
              <option value="">All vendors ({vendors.length})</option>
              {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
            </Form.Select>
          </div>
          <div className="col-md-4">
            <Form.Label className="mb-1 small fw-semibold">Payment</Form.Label>
            <Form.Select size="sm" value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}>
              <option value="">All</option>
              {PAYMENT_OPTIONS.map((o) => (
                <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-3 d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => { setFilterVendor(""); setFilterPayment(""); }}>
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Vendor table */}
      {filtered.length === 0 ? (
        <p className="text-muted">No vendors match the filter.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "0.92em" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Vendor</th>
                <th style={{ width: 100 }}>Payment</th>
                <th>Notes</th>
                <th style={{ width: 90 }}>Contacts</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const isOpen = expanded.has(v._id);
                const contactCount = (v.contacts || []).length;
                return (
                  <React.Fragment key={v._id}>
                    <tr>
                      <td className="text-center" role="button" onClick={() => toggleExpand(v._id)} style={{ cursor: "pointer", userSelect: "none" }}>
                        {isOpen ? "▼" : "▶"}
                      </td>
                      <td><strong>{v.name}</strong></td>
                      <td>
                        {v.payment_method ? (
                          <Badge bg={PAYMENT_BADGE[v.payment_method] || "secondary"} style={{ textTransform: "capitalize" }}>
                            {v.payment_method}
                          </Badge>
                        ) : "—"}
                      </td>
                      <td className="text-muted">{v.payment_notes || "—"}</td>
                      <td className="text-center">
                        <Badge bg="secondary">{contactCount}</Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1 justify-content-center">
                          <Button size="sm" variant="outline-primary" onClick={() => openEditVendor(v)} title="Edit">✏️</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleDeleteVendor(v)} title="Delete">🗑️</Button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ background: "#fafbfc", padding: "8px 16px" }}>
                          {contactCount > 0 ? (
                            v.contacts.map((c) => {
                              const isEditingThis = editingContact?.vendorId === v._id && editingContact?.contactId === c._id;
                              return isEditingThis ? (
                                <React.Fragment key={c._id}>{renderContactForm(v._id)}</React.Fragment>
                              ) : (
                                <div key={c._id} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: "0.92em" }}>
                                  <span>👤 <strong>{c.name}</strong></span>
                                  {c.role && <span className="text-muted">· {c.role}</span>}
                                  {c.phone && <span>· <a href={`tel:${c.phone}`}>📞 {c.phone}</a></span>}
                                  {c.email && <span>· <a href={`mailto:${c.email}`}>{c.email}</a></span>}
                                  <Button size="sm" variant="link" className="p-0 ms-1" onClick={() => openEditContact(v._id, c)} title="Edit contact">✏️</Button>
                                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => handleDeleteContact(v._id, c._id)} title="Remove">✕</Button>
                                </div>
                              );
                            })
                          ) : (
                            <span className="text-muted small">No contacts yet</span>
                          )}
                          {addingContactFor === v._id ? (
                            renderContactForm(v._id)
                          ) : (
                            !(editingContact?.vendorId === v._id) && (
                              <div className="mt-1">
                                <Button size="sm" variant="outline-secondary" onClick={() => openAddContact(v._id)}>+ Add Contact</Button>
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Vendor Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingVendor ? "Edit Vendor" : "Add Vendor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Vendor Name</Form.Label>
            <Form.Control
              value={vendorForm.name}
              onChange={(e) => setVendorForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Southern Glazer's"
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Payment Method</Form.Label>
            <Form.Select
              value={vendorForm.payment_method}
              onChange={(e) => setVendorForm((f) => ({ ...f, payment_method: e.target.value }))}
            >
              <option value="">— Select —</option>
              {PAYMENT_OPTIONS.map((o) => (
                <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={vendorForm.payment_notes}
              onChange={(e) => setVendorForm((f) => ({ ...f, payment_notes: e.target.value }))}
              placeholder="Net 30, COD, etc."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={handleSaveVendor}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Vendors;
